import { PERMISSIONS } from '@navicosa/shared-types';
import { Result, Spin } from 'antd';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { AdminLayout } from './layout/AdminLayout';
import { CatalogSetupPage } from './pages/catalog/CatalogSetupPage';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { ProductEditPage } from './pages/products/ProductEditPage';
import { ProductListPage } from './pages/products/ProductListPage';
import { BookingDetailPage } from './pages/bookings/BookingDetailPage';
import { BookingListPage } from './pages/bookings/BookingListPage';
import { SchedulePage } from './pages/bookings/SchedulePage';
import { CustomersPage } from './pages/customers/CustomersPage';
import { EquipmentPage } from './pages/equipment/EquipmentPage';
import { QuotationListPage } from './pages/quotations/QuotationListPage';
import { QuotationPage } from './pages/quotations/QuotationPage';
import { RequestCreatePage } from './pages/requests/RequestCreatePage';
import { RequestDetailPage } from './pages/requests/RequestDetailPage';
import { RequestListPage } from './pages/requests/RequestListPage';
import { SuppliersPage } from './pages/suppliers/SuppliersPage';
import { UsersPage } from './pages/users/UsersPage';
import { ZaloOaPage } from './pages/system/ZaloOaPage';
import { AuditPage } from './pages/system/AuditPage';
import { SettingsPage } from './pages/system/SettingsPage';

function Guard({ permission, children }: { permission?: string; children: ReactNode }) {
  const { can } = useAuth();
  if (permission && !can(permission)) {
    return (
      <Result
        status="403"
        title="Không có quyền"
        subTitle="Tài khoản của bạn không được cấp quyền xem mục này."
      />
    );
  }
  return <>{children}</>;
}

export function App() {
  const { me, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!me) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }
  const P = PERMISSIONS;
  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route index element={<DashboardPage />} />
        <Route
          path="products"
          element={
            <Guard permission={P.CATALOG_READ}>
              <ProductListPage />
            </Guard>
          }
        />
        <Route
          path="products/new"
          element={
            <Guard permission={P.CATALOG_MANAGE}>
              <ProductEditPage />
            </Guard>
          }
        />
        <Route
          path="products/:id"
          element={
            <Guard permission={P.CATALOG_READ}>
              <ProductEditPage />
            </Guard>
          }
        />
        <Route
          path="catalog"
          element={
            <Guard permission={P.CATALOG_READ}>
              <CatalogSetupPage />
            </Guard>
          }
        />
        <Route
          path="suppliers"
          element={
            <Guard permission={P.SUPPLIERS_READ}>
              <SuppliersPage />
            </Guard>
          }
        />
        <Route
          path="settings"
          element={
            <Guard permission={P.SETTINGS_READ}>
              <SettingsPage />
            </Guard>
          }
        />
        <Route
          path="audit"
          element={
            <Guard permission={P.AUDIT_READ}>
              <AuditPage />
            </Guard>
          }
        />
        <Route
          path="requests"
          element={
            <Guard permission={P.REQUESTS_READ}>
              <RequestListPage />
            </Guard>
          }
        />
        <Route
          path="requests/new"
          element={
            <Guard permission={P.REQUESTS_MANAGE}>
              <RequestCreatePage />
            </Guard>
          }
        />
        <Route
          path="requests/:id"
          element={
            <Guard permission={P.REQUESTS_READ}>
              <RequestDetailPage />
            </Guard>
          }
        />
        <Route
          path="customers"
          element={
            <Guard permission={P.CUSTOMERS_READ}>
              <CustomersPage />
            </Guard>
          }
        />
        <Route
          path="customers/:id"
          element={
            <Guard permission={P.CUSTOMERS_READ}>
              <CustomersPage />
            </Guard>
          }
        />
        <Route
          path="equipment"
          element={
            <Guard permission={P.EQUIPMENT_READ}>
              <EquipmentPage />
            </Guard>
          }
        />
        <Route
          path="users"
          element={
            <Guard permission={P.USERS_READ}>
              <UsersPage />
            </Guard>
          }
        />
        <Route
          path="integrations/zalo-oa"
          element={
            <Guard permission={P.SETTINGS_READ}>
              <ZaloOaPage />
            </Guard>
          }
        />
        <Route
          path="quotations"
          element={
            <Guard permission={P.QUOTATIONS_READ}>
              <QuotationListPage />
            </Guard>
          }
        />
        <Route
          path="quotations/:id"
          element={
            <Guard permission={P.QUOTATIONS_READ}>
              <QuotationPage />
            </Guard>
          }
        />
        <Route
          path="bookings"
          element={
            <Guard permission={P.BOOKINGS_READ}>
              <BookingListPage />
            </Guard>
          }
        />
        <Route
          path="bookings/:id"
          element={
            <Guard permission={P.BOOKINGS_READ}>
              <BookingDetailPage />
            </Guard>
          }
        />
        <Route
          path="schedule"
          element={
            <Guard permission={P.EQUIPMENT_READ}>
              <SchedulePage />
            </Guard>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
